import { Component, OnInit } from '@angular/core';
import { AppServices } from '../../services/appServices';
import { practiceSessionTemplateResponse } from '../../models/practiceSessionTemplateResponse';
import { NgFor, NgIf, DatePipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { result } from '../../models/result';
import { savePracticeSessionDrillResultsRequest } from '../../models/savePracticeSessionDrillResultsRequest';
import { savePracticeSessionRequest } from '../../models/savePracticeSessionRequest';
import { practiceSessionSearchResponse } from '../../models/practiceSessionSearchResponse';
import { searchPracticeSessionsRequest } from '../../models/searchPracticeSessionsRequest';
import { ɵEmptyOutletComponent } from '@angular/router';

@Component({
  selector: 'app-practice-session',
  standalone: true,
  imports: [NgIf, NgFor, FormsModule, DatePipe, ɵEmptyOutletComponent, NgClass],
  templateUrl: './practice-session.component.html',
  styleUrl: './practice-session.component.css'
})
export class PracticeSessionComponent implements OnInit {
  constructor(private appServices: AppServices) {}

  templates: practiceSessionTemplateResponse[] = [];
  resultOptions: result[] = [];
  practiceSessions: practiceSessionSearchResponse[] = [];
  selectedTemplate: practiceSessionTemplateResponse | null = null;
  practiceSessionId: number | null = null;
  practiceSessionDrillResults: any[] = [];

  currentDrillIndex = 0;
  currentRepIndex = 0;

  ngOnInit(): void {
    void this.searchTemplates();
    void this.searchPracticeSessions();
  }

  get sortedResultOptions() {
    return [...this.resultOptions].sort((a, b) => b.sort_order - a.sort_order);
  }

  get templateDrills() {
    return this.selectedTemplate?.template_drills ?? [];
  }

  get currentTemplateDrill() {
    if (!this.templateDrills.length) {
      return null;
    }

    if (this.currentDrillIndex < 0) {
      this.currentDrillIndex = 0;
    }

    if (this.currentDrillIndex >= this.templateDrills.length) {
      this.currentDrillIndex = this.templateDrills.length - 1;
    }

    return this.templateDrills[this.currentDrillIndex];
  }

  get currentRepNumber(): number {
    return this.currentRepIndex + 1;
  }

  get isFirstDrill(): boolean {
    return this.currentDrillIndex === 0;
  }

  get isLastDrill(): boolean {
    return this.currentDrillIndex === this.templateDrills.length - 1;
  }

  get isFirstRep(): boolean {
    return this.currentRepIndex === 0;
  }

  get isLastRep(): boolean {
    const reps = this.currentTemplateDrill?.number_of_reps ?? 0;
    return this.currentRepIndex === reps - 1;
  }

  getUsername(): string | null {
    const username = this.appServices.authenticationService.loggedInUser?.username;
    return username ?? null;
  }

  async searchTemplates(): Promise<void> {
    const username = this.getUsername();
    if (!username) return;

    this.appServices.applicationService.showGlobalSpinner();
    try {
      this.templates = await this.appServices.templateService.searchTemplates(username);
    } finally {
      this.appServices.applicationService.hideGlobalSpinner();
    }
  }

  async searchResults(assessmentArea: string): Promise<void> {
    this.appServices.applicationService.showGlobalSpinner();
    try {
      this.resultOptions = await this.appServices.lookupService.searchResults(assessmentArea);
    } finally {
      this.appServices.applicationService.hideGlobalSpinner();
    }
  }

  async searchResultsForCurrentDrill(): Promise<void> {
    const assessmentArea = this.currentTemplateDrill?.drill?.assessment_area;

    if (!assessmentArea) {
      this.resultOptions = [];
      return;
    }

    await this.searchResults(assessmentArea);
  }

  async startPracticeSession(): Promise<void> {
    const username = this.getUsername();
    if (!username || !this.selectedTemplate) return;

    this.appServices.applicationService.showGlobalSpinner();
    try {
      const dateKey = this.appServices.dataMappingService.getDateKey();
      const req: savePracticeSessionRequest = {
        session_date: new Date().toISOString(),
        username: username,
        is_deleted: false,
        practice_session_template_id: this.selectedTemplate.id,
        date_key: dateKey
      };

      this.practiceSessionId = await this.appServices.practiceService.createPracticeSession(req);
    } finally {
      this.appServices.applicationService.hideGlobalSpinner();
    }

    await this.searchResultsForCurrentDrill();
  }

  async searchPracticeSessions(): Promise<void> {
    const username = this.getUsername();
    if (!username) return;

    this.appServices.applicationService.showGlobalSpinner();
    try {
      const req: searchPracticeSessionsRequest = {
        username: username,
        pageNumber: 1,
        pageSize: 100
      };

      this.practiceSessions = await this.appServices.practiceService.searchPracticeSessions(req);
    } finally {
      this.appServices.applicationService.hideGlobalSpinner();
    }
  }

  async moveToNextDrill(): Promise<void> {
    if (this.currentDrillIndex < this.templateDrills.length - 1) {
      this.currentDrillIndex++;
      this.currentRepIndex = 0;
      await this.searchResultsForCurrentDrill();
    }
  }

  async moveToPreviousDrill(): Promise<void> {
    if (this.currentDrillIndex > 0) {
      this.currentDrillIndex--;
      this.currentRepIndex = 0;
      await this.searchResultsForCurrentDrill();
    }
  }

  async moveToNextRep(): Promise<void> {
    const reps = this.currentTemplateDrill?.number_of_reps ?? 0;

    if (this.currentRepIndex < reps - 1) {
      this.currentRepIndex++;
    } else {
      await this.moveToNextDrill();
    }
  }

  async moveToPreviousRep(): Promise<void> {
    if (this.currentRepIndex > 0) {
      this.currentRepIndex--;
    } else if (this.currentDrillIndex > 0) {
      this.currentDrillIndex--;
      const previousDrill = this.currentTemplateDrill;
      this.currentRepIndex = Math.max((previousDrill?.number_of_reps ?? 1) - 1, 0);
      await this.searchResultsForCurrentDrill();
    }
  }

  async setSelectedTemplate(template: practiceSessionTemplateResponse): Promise<void> {
    this.selectedTemplate = template;
    this.currentDrillIndex = 0;
    this.currentRepIndex = 0;
    this.practiceSessionId = null;
    this.practiceSessionDrillResults = [];
    await this.searchResultsForCurrentDrill();
  }

  async selectResult(selectedResult: result): Promise<void> {
    console.log('selected result', {
      drillIndex: this.currentDrillIndex,
      repIndex: this.currentRepIndex,
      result: selectedResult
    });

    if (!this.currentTemplateDrill || !this.practiceSessionId) return;

    const req: savePracticeSessionDrillResultsRequest = {
      results: [
        {
          drill_id: this.currentTemplateDrill.drill_id,
          practice_session_id: this.practiceSessionId,
          rep_number: this.currentRepIndex + 1,
          result_id: selectedResult.id,
          is_deleted: false
        }
      ]
    };

    await this.appServices.practiceService.savePracticeSessionDrillResult(req);

    const existingResult = this.practiceSessionDrillResults.find(
      x =>
        x.practice_session_id === this.practiceSessionId &&
        x.drill_id === this.currentTemplateDrill?.drill_id &&
        x.rep_number === this.currentRepIndex + 1
    );

    if (existingResult) {
      existingResult.result_id = selectedResult.id;
      existingResult.is_deleted = false;
    } else {
      this.practiceSessionDrillResults.push({
        id: 0,
        drill_id: this.currentTemplateDrill.drill_id,
        practice_session_id: this.practiceSessionId,
        is_deleted: false,
        rep_number: this.currentRepIndex + 1,
        result_id: selectedResult.id
      });
    }

    await this.moveToNextRep();
  }

  async selectPracticeSession(session: practiceSessionSearchResponse): Promise<void> {
    this.practiceSessionId = session.id;

    const foundTemplate = this.templates.filter(
      t => t.id === session.practice_session_template_id
    );

    if (foundTemplate.length > 0) {
      this.appServices.applicationService.showGlobalSpinner();
      try {
        this.selectedTemplate = foundTemplate[0];
        this.currentDrillIndex = 0;
        this.currentRepIndex = 0;
        this.practiceSessionDrillResults =
          await this.appServices.practiceService.searchPracticeSessionDrillResults({
            practice_session_id: this.practiceSessionId
          });
      } finally {
        this.appServices.applicationService.hideGlobalSpinner();
      }

      await this.searchResultsForCurrentDrill();
    }
  }

  isPracticeResultSelected(
    practiceSessionId: number,
    drillId: number,
    resultId: number,
    repNumber: number
  ): boolean {
    const foundResult = this.practiceSessionDrillResults.filter(
      dr =>
        dr.practice_session_id === practiceSessionId &&
        dr.drill_id === drillId &&
        dr.result_id === resultId &&
        dr.rep_number === repNumber
    );

    return !!foundResult && foundResult.length > 0;
  }
}